import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import type { OrgFeatureFlagKey } from '../utils/orgFeatureFlagKeys';
import type { IntegrationSettings } from '../services/integrationApi';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../utils/integrationSettingsCopy';

export interface ShopifyTagModeControlProps {
  tagMode: IntegrationSettings['tagMode'];
  onChange: (tagMode: 'motzu' | 'marker_only') => void;
  /** The flag that shows the control: `shopify_tag_mode` (default) or, for Lightspeed, `lightspeed_catalog_tags`. */
  flag?: OrgFeatureFlagKey;
}

/**
 * Tag mode (flag `shopify_tag_mode`): next to "Add motzu_synced tag", whether Motzu also adds its
 * generated tags or only the marker. Shared by the settings drawer, the setup walkthrough and the
 * integration dialog. Hidden with the flag off; the caller renders it for Shopify only.
 */
const ShopifyTagModeControl: React.FC<ShopifyTagModeControlProps> = ({ tagMode, onChange, flag = 'shopify_tag_mode' }) => {
  const { enabled } = useOrgFeatureFlag(flag);
  if (!enabled) return null;
  const value = tagMode === 'marker_only' ? 'marker_only' : 'motzu';
  return (
    <Box sx={{ pl: 5, pb: 1.25 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
        {COPY.tagMode.label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        aria-label={COPY.tagMode.label}
        onChange={(_event, next: 'motzu' | 'marker_only' | null) => {
          if (next) onChange(next);
        }}
      >
        <ToggleButton value="motzu" sx={{ textTransform: 'none', fontWeight: 600 }}>
          {COPY.tagMode.motzu}
        </ToggleButton>
        <ToggleButton value="marker_only" sx={{ textTransform: 'none', fontWeight: 600 }}>
          {COPY.tagMode.markerOnly}
        </ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45, mt: 0.5 }}>
        {value === 'marker_only' ? COPY.tagMode.markerOnlyDesc : COPY.tagMode.motzuDesc}
      </Typography>
    </Box>
  );
};

export default ShopifyTagModeControl;
